import * as _ from 'lodash';
import moment from 'moment';
import Joi from 'joi-browser';

import LocalStorageService from '../utilities/LocalStorageService';
import SessionStorageService from '../utilities/SessionStorageService';
import FieldApi from '../field/FieldApi';
import FieldContractorUserService from '../FieldContractorUsers';
import FieldBulletinBoardsApi from './FieldBulletinBoardsApi';

import { validationByJoi } from '../../utilities/commonValidation';
import { messageUtility } from '../../utilities/messageUtility';
import { approvalFileType } from '../../utilities/fileType';
import {
  isPrimeRoleId,
  isBranchRoleId,
  isManagerRoleId,
  roleIdToJapaneseRoleName,
} from '../../utilities/roleUtility';
import {
  deleteControlCode,
  trimAndRemoveLineBreak,
} from '../../utilities/stringUtil';

import {
  BLOCKED_ON,
  DATE_TIME_FORMAT,
  IMPORTANCE_TYPE,
  PUBLICATION_STATUS,
  BOARD_FILE,
} from '../../utilities/constants';

/**
 * 初期表示時の権限チェック
 * いずれかに該当する場合、権限エラー画面へ遷移する
 * @param {boolean} isLoadEndUsage Buildee利用終了日を超過しているかのチェックを行うかの識別用フラグ
 * @return { object } 現場情報等
 * */
const loadFieldInfo = async (isLoadEndUsage = true) => {
  const obj = {
    isAccessible: false, // 現場へのアクセス権があるか？
    isEditable: false, // 作成・編集権限があるか？
    fieldInfo: {}, // 現場の詳細
  };

  const fieldId = SessionStorageService.getFieldId();
  if (_.isEmpty(fieldId)) {
    return obj;
  }

  // 対象現場へのアクセス権が無い場合
  await FieldBulletinBoardsApi.fetchFieldBulletinBoardList(fieldId, { limit: 0 })
    .then(response => {
      if (response.result) {
        obj.isAccessible = true;
      } else {
        return obj;
      }
    })
    .catch(error => {
      return obj;
    });

  // Buildee利用終了日を過ぎている場合
  const fieldInfoResponse = await FieldApi.loadField(fieldId);
  if (!fieldInfoResponse.result) {
    return obj;
  }

  const fieldInfo = _.get(fieldInfoResponse, 'data.entities.field');
  obj.fieldInfo = fieldInfo;

  // 一覧画面の場合はBuildee利用終了日の判定をスキップ
  if (isLoadEndUsage) {
    const today = moment().format(DATE_TIME_FORMAT.slashDateFormat);
    const usageEndDate = moment(
      new Date(fieldInfo.usage_end_date).toISOString()
    ).format(DATE_TIME_FORMAT.slashDateFormat);

    // Buildee利用終了日を超過している場合
    if (moment(today).isAfter(usageEndDate)) {
      return obj;
    }
  }

  const companyInfo = fieldInfo.company;
  const companyId = Number(SessionStorageService.getCompanyId());
  const branchId = Number(SessionStorageService.getCompanyBranchId());
  const roleId = LocalStorageService.getRoleId();

  // 本社管理者の場合
  if (isPrimeRoleId(roleId)) {
    if (companyInfo.company_id === companyId) {
      obj.isEditable = true;
      return obj;
    }
  }

  // 支店管理者の場合
  if (isBranchRoleId(roleId)) {
    if (
      companyInfo.company_id === companyId &&
      companyInfo.company_branch_id === branchId
    ) {
      obj.isEditable = true;
      return obj;
    }
  }

  // 支店管理者・現場監督の場合
  if (isBranchRoleId(roleId) || isManagerRoleId(roleId)) {
    const contractResponse = await FieldContractorUserService.list(fieldId, {
      jv_company_branch_id: companyInfo.branchId,
      field_contractor_user_block_flag: BLOCKED_ON.off.id,
      user_ids: [LocalStorageService.getUserId()],
    });
    if (!contractResponse.result) {
      return obj;
    }

    if (
      _.isEmpty(contractResponse.data.entities.field_contractor_users) ||
      contractResponse.data.entities.field_contractor_users.length === 0
    ) {
      return obj;
    }

    obj.isEditable = true;
    return obj;
  }

  return obj;
};

/**
 * validation アップロードファイルのチェック
 * @param { object } files: 対象ファイル群
 * @return { object } errorInfo: 対象ファイルのindex:エラーメッセージ
 * */
const validateInputFile = async files => {
  let errorInfo = {};
  let count = 0;

  for (let key in files) {
    count++;
    const errorMessages = [];

    if (count > BOARD_FILE.COUNT) {
      errorMessages.push(messageUtility.getMessage('E-000-165-01'));
    }

    if (!files[key].alreadyUpload) {
      const fileSize = files[key].size / (1024 * 1024);
      if (fileSize > BOARD_FILE.SIZE) {
        errorMessages.push(messageUtility.getMessage('E-000-165-02'));
      }

      const isValidType = await approvalFileType(files[key], BOARD_FILE.EXT);
      if (!isValidType) {
        errorMessages.push(messageUtility.getMessage('E-000-165-03'));
      }
    }

    errorInfo = {
      ...errorInfo,
      ...{ [key]: errorMessages },
    };
  }
  return errorInfo;
};

/**
 * 添付ファイル登録API リクエスト用ファイルデータを生成
 * @param { object } file: ファイル情報
 * @return { object } fileData: 一時キー取得用ファイル情報
 * */
const createFileData = file => {
  const fileData = new FormData();
  fileData.append('file_data', file);

  return fileData;
};

/**
 * validation スキーマ作成
 * @return { object } validateSchema: バリデーション用スキーマ
 * */
const createValidateSchema = () => {
  const validateSchema = Joi.object().keys({
    title: Joi.string()
      .trim()
      .required()
      .max(255),
    contents: Joi.string()
      .trim()
      .required()
      .max(2000),
    target_partner_company_type: Joi.any().required(),
  });
  return validateSchema;
};

/**
 * 入力フォームのvalidation
 * @param { object } data: 現場掲示板お知らせオブジェクト
 * @param { string } usageEndDate: Buildee利用終了日（YYYY/MM/DD）
 * @return { object } result: バリデーション結果
 */
const validate = (data, usageEndDate) => {
  const validateSchema = createValidateSchema();
  let result = validationByJoi(validateSchema, data);
  if (
    !_.has(data, 'bulletin_board_publication_status') ||
    data.bulletin_board_publication_status == PUBLICATION_STATUS.DRAFT.id
  ) {
    if (moment(data.publication_start_at).isBefore(moment(), 'day')) {
      result.status = false;
      result.messages = {
        ...result.messages,
        publication_start_at: messageUtility.getMessage('E-000-164-01'),
      };
    } else if (
      usageEndDate !== undefined &&
      moment(new Date(data.publication_start_at).toISOString()).isAfter(
        new Date(usageEndDate).toISOString()
      )
    ) {
      result.status = false;
      result.messages = {
        ...result.messages,
        publication_start_at: messageUtility.getMessage(
          'E-000-167-03',
          usageEndDate
        ),
      };
    }
  }
  return result;
};

/**
 * 登録・更新データのmapper
 * @param { object } params: 現場掲示板お知らせオブジェクト
 * @param { number } saveType: 0:下書き（更新） 1:公開
 * @param { boolean } isCreate: true:作成用のMAP処理 false:更新用のMAP処理
 * @return { object } params: 現場掲示板お知らせオブジェクト
 * */
const createParams = (params, saveType, isCreate = true) => {
  params.title = deleteControlCode(trimAndRemoveLineBreak(params.title));
  params.contents = deleteControlCode(params.contents.trim());
  params.importance_type = params.importance_type
    ? IMPORTANCE_TYPE.CHECK
    : IMPORTANCE_TYPE.UNCHECK;
  params.target_partner_company_type = _.toNumber(
    params.target_partner_company_type
  );
  if (isCreate) {
    params.publication_status = saveType;
  } else {
    params.save_mode_type = saveType;
  }

  return params;
};

/**
 * 履歴内、roleIdの変換
 * @param {string} name ユーザ名
 * @param {number} roleId
 * @return {string} ユーザ名(ロール名)または「-」
 * */
const convertNameAndRole = (name, roleId) => {
  if (_.isEmpty(name) && _.isEmpty(roleId)) {
    return messageUtility.getMessage('I-000-166-11');
  }
  return `${name} (${roleIdToJapaneseRoleName(roleId)})`;
};

/**
 * 履歴内、日時の変換
 * @param {Date} dateTime
 * @return {Date} 変換後の日時または「-」
 * */
const convertDate = (dateTime, formatType) => {
  if (_.isEmpty(dateTime)) {
    return messageUtility.getMessage('I-000-166-11');
  }
  return moment(new Date(dateTime).toISOString()).format(formatType);
};

const FieldBulletinBoardsService = {
  loadFieldInfo,
  createValidateSchema,
  validate,
  createParams,
  validateInputFile,
  createFileData,
  convertNameAndRole,
  convertDate,
};

export default FieldBulletinBoardsService;



// WEBPACK FOOTER //
// ./src/services/fieldBulletinBoard/FieldBulletinBoardsService.js